import Sheet from 'atoms/sheet/sheet';
import * as dynamicGridSagaActions from 'models/dynamicGrid/sagaActions';
import * as dynamicGridSelectors from 'models/dynamicGrid/selector';
import * as tableColumnSagaActions from 'models/tableColumns/sagaActions';
import * as tableColumnSelectors from 'models/tableColumns/selector';
import SideCar from 'molecules/sidecar/sidecar';
import Stack from 'molecules/stack/stack';
import StackItem from 'molecules/stackItem/stackItem';
import CustomizeViewModal from 'organisms/CustomizeViewModal';
import ThetaMenu from 'organisms/thetaMenu/thetaMenu';
import UploadData from 'organisms/uploadData';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ThetaGridPaginated from 'templates/thetaGridPaginated';
import * as constants from './constants';
import Breadcrumbs from 'atoms/breadcrumbs/breadcrumbs';
import ThetaDeleteModal from 'organisms/deleteModal/deleteModal';
import { REQUESTS } from 'utils/services/thetaretail/requests';
import GridActionBar from 'atoms/GridActionBar';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowsRotate } from '@fortawesome/free-solid-svg-icons';
import { isAclUsersReadWriteEnabled, isNewUploadEnabled } from 'models/user/selector';
import Button from 'atoms/button/button';
import { CreateRowAccess } from './components/createRowAccess';
import { triggeRemovebulkAccess, triggeRemoveRowAccess } from './sagaActions';
import { triggerFetchAutoComplete } from 'models/autoComplete/sagaActions';
import { fetchBookmarksList } from 'models/bookmarks/sagaActions';
import { RowAccessInfo } from './rowAccess';
import DeleteModal from 'components/DeleteModal';
import { Button as AntButton, Tooltip } from 'antd';

export interface ModuleConfig {
	component: (props: any) => JSX.Element;
	componentId: string;
	headerName: string;
	importReducer: () => any;
	importSagas: () => any;
	isEnabled: (state: any) => boolean;
	key: string;
	name: string;
	path: string;
	routes: ModuleConfig[];
	title: string;
}

type PropsConfig = React.FunctionComponent & { breadcrumbs: ModuleConfig };

const RowAccess: React.FC<PropsConfig> = (props) => {
	const [openFilter, setOpenFilter] = React.useState<boolean>();
	const [isUploadEnabled, toggleUpload] = React.useState<boolean>(false);
	const [showModal, setShowModal] = React.useState(false);
	const [rowData, setRowData] = React.useState<RowAccessInfo | null>(null);
	const [isDeleteModal, toggleDeleteModal] = React.useState(false);
	const gridMeta = useSelector((state) => dynamicGridSelectors.getGridMetadata(state, constants.TABLE_ID));
	const [rowAccessInfo, setRowAccess] = React.useState<RowAccessInfo | null | {}>(null);
	const tableId = constants.TABLE_ID.toString();
	const dispatch = useDispatch();
	const hasWriteAccess = useSelector(isAclUsersReadWriteEnabled);
	const hasDeleteAccess = true;
	const hasSearchFilters = useSelector((state) => dynamicGridSelectors.hasSearchFilters(state, constants.TABLE_ID));

	const selectedItems = useSelector((state) => dynamicGridSelectors.getTableSelection(state, constants.TABLE_ID));

	const allAvaliableRows = useSelector((state) => dynamicGridSelectors.getGridData(state, constants.TABLE_ID));

	const selectedRows = allAvaliableRows?.filter((item) =>
		selectedItems?.selectedRowKeys?.find((o) => item.UserID === o)
	);

	const isCustomizeViewModalEnabled = useSelector((state) => tableColumnSelectors.isCustomizeViewModalEnabled(state));

	React.useEffect(() => {
		if (openFilter === undefined) {
			setOpenFilter(hasSearchFilters);
		}
	}, [hasSearchFilters]);

	useEffect(() => {
		dispatch(
			triggerFetchAutoComplete({
				componentId: 'acl-all-users',
				options: {
					service: 'acl',
					url: 'users/list',
				},
				params: { fieldGroups: ['UserEmail'] },
			})
		);

		dispatch(
			triggerFetchAutoComplete({
				componentId: 'acl-all-accessLevels',
				options: {
					service: 'acl',
					url: 'accessLevel/list',
				},
				params: { fieldGroups: ['UserEmail'] },
			})
		);
	}, [dispatch]);

	React.useEffect(() => {
		dispatch(dynamicGridSagaActions.erpEnabled({ tableId: constants.TABLE_ID }));
		dispatch(fetchBookmarksList({ tableId }));
	}, [dispatch]);

	const onRemoveRowAccess = (rowAccess: RowAccessInfo) => {
		dispatch(
			triggeRemoveRowAccess({
				userId: rowAccess?.UserID,
				selectedEntities: rowAccess?.AccessLevelEntities.map((ele) => ele?.AccessLevelEntityID),
			})
		);
		setTimeout(() => {
			dispatch(
				dynamicGridSagaActions.refreshGridData({
					tableId: Number(constants.TABLE_ID),
				})
			);
		}, 2000);
	};

	const handleRefreshClick = React.useCallback(() => {
		dispatch(
			dynamicGridSagaActions.refreshGridData({
				tableId: constants.TABLE_ID,
			})
		);
	}, [tableId, dispatch]);

	const handleResetTableColumns = React.useCallback(async () => {
		dispatch(tableColumnSagaActions.resetTableColumnsAg(constants.TABLE_ID));
	}, [dispatch, tableId]);

	const customizeViewHandler = React.useCallback((isCustomizeViewModalEnabled: boolean) => {
		dispatch(tableColumnSagaActions.setCustomizeViewModal(isCustomizeViewModalEnabled));
	}, []);

	console.log({ '>>>>>>>>': props });

	const handleMenuItemClick = React.useCallback(
		(option: string) => {
			switch (option) {
				case constants.MENU_ITEMS_VALUES.CUSTOMIZE_VIEW:
					return customizeViewHandler(true);
				case constants.MENU_ITEMS_VALUES.RESET_VIEW:
					return handleResetTableColumns();
				case constants.MENU_ITEMS_VALUES.UPLOAD_DATA:
					return toggleUpload(true);
				case constants.MENU_ITEMS_VALUES.DELETE:
					return deleteItems();
				default:
					return;
			}
		},
		[handleResetTableColumns]
	);

	const getDisabledStatus = React.useCallback(
		(option: any) => {
			switch (option.value) {
				case constants.MENU_ITEMS_VALUES.CUSTOMIZE_VIEW:
				case constants.MENU_ITEMS_VALUES.RESET_VIEW:
				case constants.MENU_ITEMS_VALUES.UPLOAD_DATA:
					return false;
				case constants.MENU_ITEMS_VALUES.DELETE:
					return !selectedItems?.selectedRowKeys?.length;
				default:
					return true;
			}
		},
		[selectedItems]
	);

	const hideCustomizeViewModal = React.useCallback(() => {
		customizeViewHandler(false);
	}, []);

	const uploadCloseClickHandler = React.useCallback(() => {
		toggleUpload(false);
	}, []);

	const handleBulkDeleteClick = () => {
		dispatch(
			triggeRemovebulkAccess({
				tableId: constants.TABLE_ID,
				ids: selectedRows
					?.map((res: RowAccessInfo) => res.AccessLevelEntities?.map((ele) => ele?.Identifier))
					.flat(),
			})
		);
		toggleDeleteModal(false);
		setTimeout(() => {
			dispatch(
				dynamicGridSagaActions.refreshGridData({
					tableId: constants.TABLE_ID,
				})
			);
		}, 2000);
	};

	const deleteItems = () => {
		toggleDeleteModal(true);
	};
	const isNewUpload = useSelector(isNewUploadEnabled);

	const gridConfig = {
		enableSelection: true,
		enableSorting: true,
		enableMultiSort: true,
		gridActions: hasWriteAccess || hasDeleteAccess,
		customActions: (hasWriteAccess || hasDeleteAccess) && {
			width: 170,
			enabled: true,
			component: ({ row }: { row: RowAccessInfo }) => {
				return (
					<Stack
						isWrap
						isGap="1rem"
						gutter="small"
						align="center"
						position="center"
						className="tbl-action-buttons"
					>
						<Button handleClick={() => onEditRow(row)}>
							{' '}
							<i className="icon-edit" />
						</Button>
						<Button
							// type="secondary"
							handleClick={() => (setShowModal(true), setRowData(row))}
						>
							<i className="icon-trash" />
						</Button>
					</Stack>
				);
			},
		},
		selectionCallBack: () => {},
	};

	const templateConfig = {
		gridConfig,
		isFilterEnabled: openFilter === undefined ? false : openFilter,
		tableId: constants.TABLE_ID,
		dataProviderOptions: {
			type: 'POST',
			...REQUESTS.POST.GET_MONGO_GRID_DETAILS_V2,
			pathParams: { tableId: constants.TABLE_ID },
			version: 2,
		},
		selectRowKey: 'UserID',
	};

	const onEditRow = (row: RowAccessInfo) => {
		setRowAccess(row);
	};

	return (
		<>
			<Stack isVertical>
				<StackItem>
					<GridActionBar>
						<div className="content-head">
							<Stack gutter="medium" align="space-between">
								<div className="content-head-lt">
									<Breadcrumbs crumbs={props.breadcrumbs} />
								</div>
								<div className="content-head-rt">
									<Stack gutter="medium" className="action-btns">
										<StackItem className="action-btn">
											<Button isPrimary handleClick={() => setRowAccess({})}>
												Create Row Access
											</Button>
										</StackItem>
										<StackItem className="action-btn">
											<Tooltip title="Refresh">
												{' '}
												<AntButton
													className="menu-action--button"
													icon={
														<FontAwesomeIcon className="font-icon" icon={faArrowsRotate} />
													}
													onClick={handleRefreshClick}
												/>
											</Tooltip>
										</StackItem>
										<StackItem className="action-btn">
											<Tooltip title="More Options">
												{' '}
												<ThetaMenu
													type="3Dots"
													options={
														hasWriteAccess
															? constants.MENU_ITEMS
															: [constants.MENU_ITEMS[0]]
													}
													handleItemClick={handleMenuItemClick}
													isDisabled={getDisabledStatus}
												/>
											</Tooltip>
										</StackItem>
									</Stack>
								</div>
							</Stack>
						</div>
					</GridActionBar>
				</StackItem>
				<StackItem isStretch isGrow>
					<Sheet isPage className="order-tbl">
						<Stack isVertical gutter="medium">
							<StackItem isGrow isStretch>
								<ThetaGridPaginated config={templateConfig} />
							</StackItem>
						</Stack>
					</Sheet>
				</StackItem>
			</Stack>

			{rowAccessInfo && <CreateRowAccess rowAccessInfo={rowAccessInfo} onHide={() => setRowAccess(null)} />}

			{isCustomizeViewModalEnabled && (
				<CustomizeViewModal hideModal={hideCustomizeViewModal} tableId={constants.TABLE_ID} />
			)}
			{isUploadEnabled && (
				<SideCar isActive={isUploadEnabled} handleOnClose={uploadCloseClickHandler}>
					<UploadData tableId={tableId} />
				</SideCar>
			)}
			{isDeleteModal && (
				<ThetaDeleteModal
					hideModal={() => {
						toggleDeleteModal(false);
					}}
					modalTitle={'Data Restrictions'}
					title={` (${
						selectedItems?.isSelectAll ? 'All' : selectedItems?.selectedRowKeys?.length
					}) selected  Data Restriction${selectedItems?.selectedRowKeys?.length > 1 ? 's' : ''}`}
					handleDeleteClick={handleBulkDeleteClick}
				/>
			)}
			{showModal && (
				<DeleteModal
					title={'Data Restriction'}
					success={() => {
						const deleteRowData = rowData as RowAccessInfo;
						onRemoveRowAccess(deleteRowData);
					}}
					hideModal={() => setShowModal(false)}
				/>
			)}
		</>
	);
};
export default RowAccess;
